import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Bell, X, AlertTriangle, Calendar, Flag, MapPin, CheckCheck, MessageSquare, Volume2, VolumeX } from 'lucide-react';
import { playNotificationSound, isSoundMuted, toggleSoundMuted } from '../../utils/notificationSound';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const ALERT_ICONS = {
  new_booking: Calendar,
  new_dispute: AlertTriangle,
  user_report: Flag,
  address_change_request: MapPin,
  support_reply: MessageSquare,
};

const ALERT_COLORS = {
  new_booking: 'text-blue-500 bg-blue-50',
  new_dispute: 'text-amber-500 bg-amber-50',
  user_report: 'text-red-500 bg-red-50',
  address_change_request: 'text-violet-500 bg-violet-50',
  support_reply: 'text-emerald-500 bg-emerald-50',
};

export default function AdminAlertBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [muted, setMuted] = useState(isSoundMuted());
  const wsRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/alerts?limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(res.data.alerts || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) {
      console.error('AdminAlertBell: failed to fetch alerts', e);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [user?.id, fetchAlerts]);

  // WebSocket for real-time alerts
  useEffect(() => {
    if (!user?.id) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/admin/${user.id}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'admin_alert' && data.alert) {
          setAlerts(prev => [data.alert, ...prev].slice(0, 15));
          setUnreadCount(prev => prev + 1);
          playNotificationSound();
        }
        // Notify the support unread hook (sidebar/mobile badges) in real time
        if (data.type === 'support_message' || data.type === 'support_assignment') {
          window.dispatchEvent(new CustomEvent('support-message'));
          if (data.type === 'support_message' && data.message?.sender_type !== 'staff') {
            playNotificationSound();
          }
        }
      } catch (e) {
        console.warn('AdminAlertBell: failed to parse websocket message', e);
      }
    };

    ws.onerror = () => {};
    ws.onclose = () => {};

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [user?.id]);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/alerts/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    } catch (e) {
      console.error('AdminAlertBell: failed to mark alerts as read', e);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative" data-testid="admin-alert-bell">
      <button
        onClick={() => { setShowPanel(!showPanel); if (!showPanel) fetchAlerts(); }}
        className="relative w-9 h-9 rounded-lg hover:bg-slate-700/50 flex items-center justify-center transition-colors"
        data-testid="admin-alert-toggle"
      >
        <Bell className="w-5 h-5 text-slate-300" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse"
            data-testid="admin-alert-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
          <div
            className="absolute left-full ml-3 top-0 w-80 bg-slate-800 rounded-xl shadow-2xl border border-slate-700 z-50 max-h-[28rem] overflow-hidden"
            data-testid="admin-alert-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <h3 className="font-semibold text-white text-sm">Alerts</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMuted(toggleSoundMuted())}
                  className="text-slate-400 hover:text-slate-200 p-0.5"
                  title={muted ? 'Unmute alert sound' : 'Mute alert sound'}
                  data-testid="admin-sound-toggle"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-amber-400 hover:text-amber-300 font-medium flex items-center gap-1"
                    data-testid="admin-mark-all-read"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Read all
                  </button>
                )}
                <button onClick={() => setShowPanel(false)} className="text-slate-400 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[24rem]">
              {alerts.length === 0 ? (
                <p className="text-center py-10 text-slate-500 text-sm">No alerts yet</p>
              ) : (
                alerts.map(a => {
                  const Icon = ALERT_ICONS[a.type] || Bell;
                  const colorClass = ALERT_COLORS[a.type] || 'text-slate-400 bg-slate-700';
                  return (
                    <div
                      key={a.id}
                      onClick={a.type === 'support_reply' ? () => { setShowPanel(false); navigate('/admin/messages'); } : undefined}
                      className={`px-4 py-3 border-b border-slate-700/50 transition-colors ${!a.read && !a.is_read ? 'bg-slate-750 bg-opacity-60' : ''} ${a.type === 'support_reply' ? 'cursor-pointer hover:bg-slate-700/40' : ''}`}
                      data-testid={`admin-alert-item-${a.id}`}
                    >
                      <div className="flex gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-200 truncate">{a.title}</p>
                            {!a.read && !a.is_read && <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />}
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{a.message}</p>
                          <p className="text-[10px] text-slate-500 mt-1">{formatTime(a.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
